import React, { useState, useEffect, useCallback } from 'react';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Row, Col, Button, FormGroup, Label, InputGroup, Input, Form } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import NumberFormControl from '../components/numberFormControl';
import TextFormControl from '../components/textFormControl';
import { withGetData } from '../../main/hoc/withGetData';
import * as notify from '../../main/utils/notify';
import VariablesForm from './variablesForm';
import useForm from '../hooks/useForm';
import Select from 'react-select';
import ConfirmationModal from '../components/confirmationModal';
import {
  getInputType,
  getIdsFieldsFromParams,
  getFormControls,
  getFiltersFromParams,
  getUrlParamsFromFormControls,
  getMachinesFromParams
} from '../utils';
import { variablesTypes } from '../constants';
import Loading from '../../main/components/loading';
import { ErrorBoundary } from '../../main/hoc/errorboundary';

const FiltersForm = ({ variables, machines, onOpenHistogramModal, formControls, setFormControls, initialFiltersValues, defaultFormControls }) => {
  const [ allVariables, setAllVariables ] = useState([]);
  const [ machinesOptions, setMachinesOptions ] = useState([]);
  const [ selectedMachines, setSelectedMachines ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ confirmationModal, setConfirmationModal ] = useState({
    isOpen: false,
    onConfirm: () => { }
  });
  const { fields, handleFieldChange, clearFields, setFields, removeFields } = useForm({
    start_date: '',
    end_date: '',
    pulse: '',
    run: ''
  });
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (variables.data && allVariables.length === 0) {
      const options = variables.data.map((option) => option);
      setAllVariables(options);
    }
  }, [ variables, allVariables ]);

  useEffect(() => {
    if (machines.data && machinesOptions.length === 0) {
      const options = machines.data.map(name => ({
        value: name,
        label: name
      }));
      if (options.length) setMachinesOptions(options);
    }
  }, [ machines, machinesOptions ]);

  useEffect(() => {
    setFields(initialFiltersValues);
  }, [ initialFiltersValues, setFields ]);

  useEffect(() => {
    const search = location.search;

    if (search) {
      setIsLoading(true);
      if (allVariables.length) {
        const params = new URLSearchParams(search);
        
        if (params.has('varIds')) {
          const varIds = getIdsFieldsFromParams(params);
          const newFormControls = getFormControls(varIds, variablesTypes, allVariables);
          setFormControls(newFormControls);
        }

        setSelectedMachines(getMachinesFromParams(params));
        setFields(getFiltersFromParams(params));
        setIsLoading(false);
      }
    }
  }, [ location, allVariables, setFields, setFormControls ]);

  const onAddFormControl = useCallback(async (selectedOption) => {
    const variable = allVariables.find(({ ids_field }) => ids_field === selectedOption);

    if (!variable) {
      notify.error(
        t('filters_form.notifications.ids_field_does_not_exist.title'),
        t('filters_form.notifications.ids_field_does_not_exist.body')
      );
    }
    else {
      const formControlExists = formControls.find(({ ids_field }) => ids_field === variable.ids_field);

      if (formControlExists) {
        notify.warning(
          t('filters_form.notifications.form_field_exists.title'),
          t('filters_form.notifications.form_field_exists.body')
        );
      }
      else {
        const variableType = variablesTypes[variable.type];
        const updatedFormControls = [ 
          ...formControls, 
          { cType: getInputType(variableType.value), ...variable }
        ];

        setFormControls(updatedFormControls);
        notify.success(
          t('filters_form.notifications.form_field_created.title'),
          t('filters_form.notifications.form_field_created.body')
        );
      }
    }
  }, [ formControls, allVariables, t, setFormControls ]);

  const onDeleteFormControl = (ids_field) => {
    const updatedFormControls = formControls.filter(item => item.ids_field !== ids_field);
    setFormControls(updatedFormControls);
    removeFields(ids_field);
    setConfirmationModal({
      isOpen: false,
      onConfirm: () => { }
    });
  };

  const handleReset = () => {
    setSelectedMachines([]);
    clearFields();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formControls.length !== 0) {
      const params = new URLSearchParams();

      for (let field in fields) {
        params.append(field.replace(/\//g, '.'), fields[field]);
      }

      if (selectedMachines) {
        const machinesList = selectedMachines.map(({ value }) => value).join('.');
        params.append('machines', machinesList);
      }

      params.append('varIds', getUrlParamsFromFormControls(formControls));
      history.push(`/experiments?${params.toString()}`);
    }
    else {
      notify.warning(
        t('filters_form.notifications.empty_form.title'),
        t('filters_form.notifications.empty_form.body')
      );
    }
  };

  const handleResetToDefaults = () => {
    setFormControls(defaultFormControls);
    clearFields();
    const params = new URLSearchParams();
    params.append('varIds', getUrlParamsFromFormControls(defaultFormControls));
    history.push(`/experiments?${params.toString()}`);
  };

  return (
    <>
      <ConfirmationModal
        confirmationModal={confirmationModal}
        setConfirmationModal={setConfirmationModal}
      />
      <Col>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t('date_label')}</Label>
            <InputGroup>
              <Input
                type='date'
                name='start_date'
                value={fields.start_date}
                onChange={handleFieldChange}
              />
              <Input
                type='date'
                name='end_date'
                value={fields.end_date}
                onChange={handleFieldChange}
              />
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Label>{t('machine_label')}</Label>
            <Select
              onChange={(options) => setSelectedMachines(options)}
              options={machinesOptions}
              isMulti={true}
              placeholder='Any'
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: '#3071b3'
                }
              })}
              styles={{
                container: (provided) => ({
                  ...provided,
                  zIndex: '1000'
                })
              }}
            />
          </FormGroup>
          <FormGroup className="d-flex">
            <div className='w-100'>
              <Label>Pulse</Label>
              <Input
                type='number'
                name='pulse'
                value={fields.pulse}
                onChange={handleFieldChange}
              />
            </div>
            <div className='w-100'>
              <Label>Run</Label>
              <Input
                type='number'
                name='run'
                value={fields.run}
                onChange={handleFieldChange}
              />
            </div>
          </FormGroup>
          {isLoading ? (
            <Loading className='text-center my-3' />
          ) : (
            <TransitionGroup>
              {formControls.map(item => (
                <CSSTransition
                  key={item.id}
                  timeout={400}
                  classNames='list-animation-item'
                >
                  {item.cType === 'text' ? (
                    <TextFormControl
                      label={item.name || item.ids_field}
                      name={item.id}
                      value={fields[item.id] || ''}
                      onChange={handleFieldChange}
                      onDelete={() => {
                        setConfirmationModal({
                          isOpen: true,
                          onConfirm: () => onDeleteFormControl(item.ids_field)
                        });
                      }}
                    />
                  ) : (
                    <NumberFormControl
                      label={(item.name || item.ids_field)+` [${item.unit || ' '}]`}
                      idsField={item.ids_field}
                      variable_id={item.id}
                      machines={selectedMachines.map(elem => elem.value)}
                      unit={item.unit}
                      minValue={fields[`${item.id}_min`] || ''}
                      maxValue={fields[`${item.id}_max`] || ''}
                      minValueName={`${item.id}_min`}
                      maxValueName={`${item.id}_max`}
                      variableTypeId={variablesTypes[item.type]?.type}
                      onChange={handleFieldChange}
                      onDelete={() => {
                        setConfirmationModal({
                          isOpen: true,
                          onConfirm: () => onDeleteFormControl(item.ids_field)
                        });
                      }}
                      onOpenHistogramModal={onOpenHistogramModal}
                    />
                  )}
                </CSSTransition>
              ))}
            </TransitionGroup>
          )}
          <Row className='d-flex my-2'>
            <Col>
              <Button
                type='submit'
                block
                color='primary'
              >
                {t('filters_form_search_button')}
              </Button>
            </Col>
            <Col>
              <Button
                type='button'
                block
                color='primary'
                onClick={handleReset}
              >
                {t('filters_form_reset_button')}
              </Button>
            </Col>
          </Row>
        </Form>
        <VariablesForm variables={allVariables} onSubmit={onAddFormControl} />
        <Button onClick={handleResetToDefaults} block>{t('reset_to_defaults_button')}</Button>
      </Col>
    </>
  );
};

FiltersForm.propTypes = {
  variables: PropTypes.object.isRequired,
  defaultFormControls: PropTypes.array.isRequired,
  machines: PropTypes.object.isRequired, // HOC
  onOpenHistogramModal: PropTypes.func.isRequired,
  initialFiltersValues: PropTypes.object.isRequired,
  formControls: PropTypes.array.isRequired,
  setFormControls: PropTypes.func.isRequired,
};

export default compose(
  withGetData({
    machines: '/experiments/machines'
  }),
  ErrorBoundary((props) => props.t('components.filters_form'))
)(FiltersForm);